import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import moment from 'moment';

import {apiGetQuoteDashboard} from 'constants/Api';
import {IQuoteSummary, IQuoteConversionRate} from 'constants/interfaces';

import './QuoteSummaryWidget.scss';

const PIE_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

export default function QuoteSummaryWidget(): React.ReactElement {
  const [data, setData] = useState<{
    summary: IQuoteSummary;
    conversion: IQuoteConversionRate;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    apiGetQuoteDashboard()
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err) => {
        console.error('Failed to load quote dashboard for widget', err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  if (loading) {
    return <Skeleton variant="rect" height={500} style={{borderRadius: 8}} />;
  }

  if (!data) {
    return (
      <Box className="quote-summary-widget">
        <Typography className="widget-heading">QUOTE SUMMARY</Typography>
        <Typography variant="body2" color="textSecondary">
          Could not load quote data.
        </Typography>
      </Box>
    );
  }

  const {summary, conversion} = data;

  return (
    <Box className="quote-summary-widget">
      <Typography className="widget-heading">QUOTE SUMMARY</Typography>

      <Box className="conversion-row">
        <Box className="stat">
          <Typography className="val">
            {conversion.totalQuotes.toLocaleString()}
          </Typography>
          <Typography className="lbl">Total Quotes</Typography>
        </Box>
        <Box className="stat">
          <Typography className="val">
            {conversion.acceptedQuotes.toLocaleString()}
          </Typography>
          <Typography className="lbl">Accepted</Typography>
        </Box>
        <Box className="stat highlight">
          <Typography className="val">
            {conversion.conversionRate.toFixed(1)}%
          </Typography>
          <Typography className="lbl">Conversion</Typography>
        </Box>
      </Box>

      {summary.statusCounts?.length > 0 && (
        <Box className="status-counts">
          {summary.statusCounts.map((sc, idx) => (
            <Box key={idx} className="status-badge">
              <Typography className="count">{sc.count}</Typography>
              <Typography className="description">{sc.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box className="charts-row">
        {summary.depotBreakdown?.length > 0 && (
          <Box className="chart-box">
            <Typography className="chart-title">
              Quotes by Area/Depot
            </Typography>
            <Box className="chart-wrapper">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={summary.depotBreakdown}
                    dataKey="count"
                    nameKey="depotName"
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    fill="#8884d8"
                    label
                  >
                    {summary.depotBreakdown.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}

        {summary.valueTimeline?.length > 0 && (
          <Box className="chart-box">
            <Typography className="chart-title">Value Timeline</Typography>
            <Box className="chart-wrapper">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={summary.valueTimeline}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(tick) => moment(tick).format('DD MMM')}
                    fontSize={10}
                  />
                  <YAxis
                    tickFormatter={(tick) => `R${tick / 1000}k`}
                    fontSize={10}
                    width={40}
                  />
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                    labelFormatter={(label) =>
                      moment(label).format('DD MMM YYYY')
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="totalValue"
                    stroke="#0b4a45"
                    strokeWidth={2}
                    dot={{r: 3}}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}
      </Box>

      {summary.needsAttention?.length > 0 && (
        <Box className="attention-section">
          <Typography className="section-title">
            Expiring in 7 Days (Needs Attention)
          </Typography>
          <Box className="attention-list">
            {summary.needsAttention.map((quote, idx) => (
              <Box key={idx} className="attention-card">
                <Box className="row-top">
                  <Typography className="farmer-name">
                    {quote.farmer?.name} {quote.farmer?.surname}
                  </Typography>
                  <Typography className="quote-number">
                    {quote.quoteNumber || 'N/A'}
                  </Typography>
                </Box>
                <Box className="row-bottom">
                  <Typography className="cost">
                    {formatCurrency(quote.total)}
                  </Typography>
                  <Typography className="date">
                    {moment(quote.insertedDate).format('DD MMM YYYY')}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
